import {
	RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { DayFocus, DayGroup } from "../utils/eventDays";
import { useOnVisible } from "./useOnVisible";

/**
 * The slice of days a browse view has mounted, growing at BOTH ends as the
 * person scrolls, and asking for another month once it runs out either way.
 *
 * ## Why a window rather than "render the first N"
 *
 * The first version of this rendered a prefix — the first 60 events, then 120,
 * and so on — which is fine as long as time only runs forwards. It cannot load
 * EARLIER months: prepending August to a prefix showing September means the
 * first 60 events are now August's, and everything the person was reading is
 * unmounted underneath them. So the slice has to be bounded at both ends.
 *
 * ## Why the bounds are day keys, not indices
 *
 * Loading an earlier month prepends to the array, so every index shifts and a
 * window remembered as "items 0 to 60" silently becomes a different sixty. Day
 * keys (`YYYY-MM-DD`) do not move when a month arrives in front of them, so the
 * window survives a page landing at either end without any of the arithmetic
 * that index-based windows need.
 *
 * ## Scrolling up has to hold its place
 *
 * Adding content ABOVE the viewport pushes what is on screen down by its height.
 * `overflow-anchor` handles this in Chrome and Firefox and does nothing in
 * Safari, so the compensation is explicit: the scroll height is measured before
 * the growth and the difference is added back to `scrollTop` in a layout effect,
 * before the browser paints.
 */

/** Events added per step, at either end. Whole days, so this is approximate. */
const STEP = 60;

interface Options {
	/** Every day in hand, oldest first. */
	days: DayGroup[];
	/** The element holding the rendered days; its scrollable ancestor is the box. */
	rootRef: RefObject<HTMLElement | null>;
	/** Place the window at this day rather than at the oldest one. See DayFocus. */
	focus?: DayFocus | null;
	/** Fetch the month before the earliest day in hand. */
	onNeedEarlier?: () => void;
	/** Fetch the month after the latest day in hand. */
	onNeedLater?: () => void;
	loadingEarlier?: boolean;
	loadingLater?: boolean;
}

interface Result {
	/** The days to render. */
	window: DayGroup[];
	/** Sentinels: mount these above and below the rendered days. */
	topRef: ReturnType<typeof useOnVisible>;
	bottomRef: ReturnType<typeof useOnVisible>;
	/** True when the window reaches that end of the days in hand. */
	atStart: boolean;
	atEnd: boolean;
	/**
	 * Is the panel long enough to scroll right now?
	 *
	 * Reported because the sentinels below fire only while it is, so a view that
	 * fits in the panel reaches its ends and stops. The edges use this to offer a
	 * button instead of an instruction to scroll — see browseEdges.
	 */
	canScroll: boolean;
}

/**
 * Whole days between two `YYYY-MM-DD` keys, unsigned.
 *
 * Parsed as UTC rather than local so the subtraction cannot land on a daylight
 * saving boundary and come back 0.96 of a day. `unscheduled` is not a date and
 * parses to NaN, which compares false against everything — so an unscheduled
 * group never wins "nearest", which is right: it has no distance from today.
 */
function daysApart(a: string, b: string): number {
	const ms = Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`);
	return Math.abs(ms) / 86400000;
}

/** Grow `from` backwards until about STEP events are added. Returns an index. */
function extendBack(days: DayGroup[], from: number): number {
	let index = from;
	let added = 0;
	while (index > 0 && added < STEP) {
		index -= 1;
		added += days[index].events.length;
	}
	return index;
}

/** Grow `to` forwards until about STEP events are added. Returns an index. */
function extendForward(days: DayGroup[], to: number): number {
	let index = to;
	let added = 0;
	while (index < days.length - 1 && added < STEP) {
		index += 1;
		added += days[index].events.length;
	}
	return index;
}

export function useDayWindow({
	days,
	rootRef,
	focus,
	onNeedEarlier,
	onNeedLater,
	loadingEarlier,
	loadingLater,
}: Options): Result {
	/* The bounds, as day keys. Null means "not placed yet", which is the state
	   before any data has arrived and after the anchor moves. */
	const [fromKey, setFromKey] = useState<string | null>(null);
	const [toKey, setToKey] = useState<string | null>(null);

	/** Scroll height captured just before growing upwards. See the note above. */
	const heldHeight = useRef<number | null>(null);

	const box = useCallback(
		() => rootRef.current?.closest(".em-browse-body") ?? null,
		[rootRef],
	);

	/* Where the remembered bounds sit in the days we now hold. -1 means the key
	   is gone — the data was replaced under us (a new month picked, a filter
	   ticked) — and the window is placed again from scratch. */
	const fromIndex = fromKey ? days.findIndex((d) => d.key === fromKey) : -1;
	const toIndex = toKey ? days.findIndex((d) => d.key === toKey) : -1;
	const placed = fromIndex !== -1 && toIndex !== -1 && toIndex >= fromIndex;

	/** The last focus request SETTLED. See DayFocus on why this is the seq. */
	const handledSeq = useRef<number | null>(null);

	/**
	 * The last request placed at all, settled or not. See `covers`.
	 */
	const triedSeq = useRef<number | null>(null);

	/**
	 * Do the days in hand actually REACH the day being asked for?
	 *
	 * This is the difference between an answer and a guess. `indexOfDay` always
	 * returns an index, and when every day in hand falls before the requested
	 * one it returns the LAST of them — sensible as a holding position, wrong as
	 * a final answer, because the day being asked for may simply not have loaded
	 * yet.
	 *
	 * Marking a request handled on that guess is what stopped the browse views
	 * landing on today. The window is placed from the first month that arrives,
	 * today is often past the end of it, so it settled on the last day of that
	 * month and recorded the request as done — and the months that arrived a
	 * moment later, today among them, were never looked at.
	 */
	const covers = useCallback(
		(list: DayGroup[], day: string) => list.some((d) => d.key >= day),
		[],
	);

	/**
	 * Where a day sits among the days in hand: that day, or the first after it.
	 *
	 * "The first day AFTER it" is the part that matters: Today is pressed on
	 * plenty of days that have no events, and placing the window nowhere would
	 * read as the button being broken. Day keys are `YYYY-MM-DD`, so a string
	 * comparison orders them and the days are already in date order. Past every
	 * day in hand, it settles on the last one, which is as close to the request
	 * as this data gets.
	 *
	 * `nearest` asks the other question: the closest day in EITHER direction.
	 * A search's results are not a calendar page, they are wherever the term
	 * matched, so "the first one on or after today" can be six months out while
	 * a match from last Friday sits just above it. Only the two candidates
	 * either side of the boundary can win, so this stays one comparison rather
	 * than a scan.
	 */
	const indexOfDay = useCallback(
		(list: DayGroup[], day: string, nearest?: boolean) => {
			const found = list.findIndex((d) => d.key >= day);
			if (found === -1) return list.length - 1;
			if (!nearest || found === 0) return found;
			const before = daysApart(list[found - 1].key, day);
			const after = daysApart(list[found].key, day);
			/* `unscheduled` sorts after every date key, so it can be the day
			   `found` landed on when every real match is in the past. It has no
			   distance from today, so the dated neighbour wins by default. */
			if (Number.isNaN(after)) return found - 1;
			// Ties go forward, which is the same bias the default has.
			return before < after ? found - 1 : found;
		},
		[],
	);

	const place = useCallback(
		(list: DayGroup[], start: number) => {
			setFromKey(list[start].key);
			setToKey(list[extendForward(list, start)].key);
		},
		[],
	);

	/**
	 * Place the window: at the focus day when there is one, otherwise at the
	 * start. Runs whenever the bounds cannot be found among the days in hand,
	 * which covers first load, a change of month, and a change of filters.
	 */
	useEffect(() => {
		if (days.length === 0 || placed) return;
		/* An UNHANDLED request only. A handled one is a record of where Today put
		   the window some time ago, and the window can come unplaced long after
		   that — `maxPages` evicting the month being read is the common way. Left
		   in, that eviction would drag the reader back towards a Today they
		   pressed several months ago instead of leaving them where they scrolled
		   to. */
		if (focus && handledSeq.current !== focus.seq) {
			/* Placed either way — a reader waiting on data should see the closest
			   thing to their request, not the oldest month in hand — but only
			   RECORDED as handled once the days actually reach it. Until then it
			   stays open for the effect below to correct when the rest arrives. */
			triedSeq.current = focus.seq;
			if (covers(days, focus.day)) handledSeq.current = focus.seq;
			place(days, indexOfDay(days, focus.day, focus.nearest));
			return;
		}
		place(days, 0);
	}, [days, placed, focus, place, indexOfDay, covers]);

	/**
	 * Re-place when a request arrives on a window that is already placed.
	 *
	 * Two cases, and they look the same from here. A NEW request: Today pressed
	 * while already reading this month. And a request placed only PROVISIONALLY,
	 * because the days in hand did not reach the day it asked for — now that more
	 * have loaded, they may.
	 *
	 * The request is marked handled only once it has actually been acted on. An
	 * earlier version marked it on the way past while the month was still
	 * loading, so when the days did arrive the effect had already written the
	 * request off and Today did nothing at all.
	 *
	 * ONE PROVISIONAL PLACEMENT PER REQUEST, which is what `triedSeq` records.
	 * Without it this would re-place and scroll to the top on every change to
	 * `days` for as long as the request went uncovered — and an event list with
	 * nothing at all after today never gets covered, so scrolling up to load an
	 * earlier month would yank the reader back to the top each time.
	 */
	useEffect(() => {
		if (!focus || days.length === 0) return;
		if (handledSeq.current === focus.seq) return;

		const reached = covers(days, focus.day);
		if (!reached && triedSeq.current === focus.seq) return;

		triedSeq.current = focus.seq;
		if (reached) handledSeq.current = focus.seq;
		place(days, indexOfDay(days, focus.day, focus.nearest));
		box()?.scrollTo({ top: 0 });
	}, [focus, days, box, place, indexOfDay, covers]);

	const atStart = placed && fromIndex === 0;
	const atEnd = placed && toIndex === days.length - 1;

	/**
	 * Is the panel long enough to have been scrolled?
	 *
	 * Guards THE FETCH, at both ends, and only the fetch. A view narrowed to a
	 * handful of events does not fill the panel, so its sentinels are on screen
	 * from the moment they render — and since the month that arrives is dropped
	 * again at the far end by `maxPages`, the content never outgrows the panel
	 * either. Without this it would page through the calendar for ever with
	 * nobody touching the wheel.
	 *
	 * It deliberately does NOT guard growing the window over days already in
	 * hand. That branch terminates on its own — it stops at either end of `days`
	 * and fetches nothing — and a panel that has not been filled is precisely
	 * when more of what is already loaded should be mounted. Guarding it too
	 * meant a short window over a long result set could sit there showing sixty
	 * of nine hundred rows with no way to reach the rest.
	 *
	 * At the ends, the edges offer a button rather than an instruction to scroll
	 * a box that cannot scroll. See browseEdges and `canScroll`.
	 */
	const scrollable = useCallback(() => {
		const el = box();
		return Boolean(el && el.scrollHeight > el.clientHeight);
	}, [box]);

	const topRef = useOnVisible(() => {
		if (!placed) return;
		if (fromIndex > 0) {
			const el = box();
			heldHeight.current = el ? el.scrollHeight : null;
			setFromKey(days[extendBack(days, fromIndex)].key);
			return;
		}
		if (scrollable() && !loadingEarlier) onNeedEarlier?.();
	}, placed && !loadingEarlier);

	const bottomRef = useOnVisible(() => {
		if (!placed) return;
		if (toIndex < days.length - 1) {
			setToKey(days[extendForward(days, toIndex)].key);
			return;
		}
		if (scrollable() && !loadingLater) onNeedLater?.();
	}, placed && !loadingLater);

	/**
	 * Put the reading position back after growing upwards.
	 *
	 * A layout effect, not an effect: this has to happen between React writing
	 * the rows and the browser painting them, or the content visibly jumps.
	 */
	useLayoutEffect(() => {
		const before = heldHeight.current;
		if (before === null) return;
		heldHeight.current = null;
		const el = box();
		if (!el) return;
		const delta = el.scrollHeight - before;
		if (delta > 0) el.scrollTop += delta;
	});

	/**
	 * The measurement, as state rather than a call, for anything rendering.
	 *
	 * A layout effect with no dependency array: it has to re-measure after every
	 * render, because what changes the answer is the CONTENT — a month arriving,
	 * a filter narrowing, a day section collapsing — and none of that is a value
	 * this hook is handed. The set is guarded on the value actually changing, so
	 * it settles on the first pass instead of looping.
	 *
	 * The resize listener covers the one way the answer changes with no render
	 * behind it: the same rows in a shorter window.
	 */
	const [canScroll, setCanScroll] = useState(false);

	const measure = useCallback(() => {
		const next = scrollable();
		setCanScroll((prev) => (prev === next ? prev : next));
	}, [scrollable]);

	useLayoutEffect(measure);

	useEffect(() => {
		globalThis.addEventListener("resize", measure);
		return () => globalThis.removeEventListener("resize", measure);
	}, [measure]);

	/**
	 * Fill the panel when scrolling cannot.
	 *
	 * The sentinels are driven by an IntersectionObserver, which reports a
	 * CHANGE of intersection. In a panel long enough to scroll that is exactly
	 * right: an edge leaves the viewport as the window grows and comes back when
	 * the reader arrives at it again, one growth per visit.
	 *
	 * In a panel the content does not fill, the edge never leaves the viewport,
	 * so it is reported once and never again. The window grows a single step and
	 * stops, and the edge goes on saying "scroll up for earlier days" in a box
	 * with no scrollbar — which is precisely the state a search lands in, since
	 * it opens in the MIDDLE of its results, at the match nearest today, with
	 * earlier days above it and nothing to scroll to reach them.
	 *
	 * So while the panel does not scroll, the window is grown here instead:
	 * measured, not observed. It terminates on its own. Each pass moves one end
	 * strictly outwards, and both are guarded at the ends of `days`, so it stops
	 * when the panel starts scrolling or when everything in hand is mounted.
	 *
	 * FETCHING is not done here and must not be. That stays on the sentinels,
	 * gated on `scrollable()`, because a fetch replaces the days rather than
	 * mounting more of them and `maxPages` drops one at the far end for each one
	 * gained — the loop that guard exists to prevent.
	 *
	 * ## Downwards first, and the reason the order matters
	 *
	 * The window is placed with the requested day FIRST, so it sits at the top
	 * of the panel. Growing upwards from there prepends days above it and pushes
	 * it down; growing downwards adds the days after it, which is where a reader
	 * who asked for today is going to look next. So the panel is filled forwards
	 * until there is nothing left to add, and only then backwards.
	 *
	 * Backwards growth also has to hold the reading position, exactly as the top
	 * sentinel does: capture the height before the days are prepended and let the
	 * layout effect below add the difference back. Without it the list opened
	 * about sixty events above today — close, which is what made it hard to see:
	 * it looked like the placement was slightly wrong rather than like something
	 * had scrolled the panel afterwards.
	 */
	useEffect(() => {
		if (!placed || canScroll) return;
		if (toIndex < days.length - 1) {
			setToKey(days[extendForward(days, toIndex)].key);
			return;
		}
		if (fromIndex > 0) {
			const el = box();
			heldHeight.current = el ? el.scrollHeight : null;
			setFromKey(days[extendBack(days, fromIndex)].key);
		}
	}, [placed, canScroll, fromIndex, toIndex, days, box]);

	const window = useMemo(
		() => (placed ? days.slice(fromIndex, toIndex + 1) : []),
		[placed, days, fromIndex, toIndex],
	);

	return { window, topRef, bottomRef, atStart, atEnd, canScroll };
}
